// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type {
  MiddlewareState,
  OffsetOptions as LibraryOffsetOptions,
  ReferenceElement,
} from '@floating-ui/dom'
import type { Except, Replace } from 'type-fest'

import type { LinkIcon } from './components/shared'

export type Attributes = Record<string, string | undefined>

const inheritAttributes = (
  element: Element,
  attributes: string[] = [],
): Attributes => {
  const attributeObject: Attributes = {}

  for (const attribute of attributes) {
    const value = element.getAttribute(attribute)
    if (value !== null) {
      attributeObject[attribute] = value
      element.removeAttribute(attribute)
    }
  }

  return attributeObject
}

type ARIAAttribute =
  | Replace<Lowercase<keyof ARIAMixin>, 'aria', 'aria-'>
  | 'aria-activedescendant'
  | 'aria-braillelabel'
  | 'aria-brailleroledescription'
  | 'aria-colindextext'
  | 'aria-controls'
  | 'aria-describedby'
  | 'aria-description'
  | 'aria-details'
  | 'aria-errormessage'
  | 'aria-flowto'
  | 'aria-labelledby'
  | 'aria-owns'
  | 'aria-relevant'
  | 'aria-rowindextext'

const ariaAttributes: ARIAAttribute[] = [
  'aria-activedescendant',
  'aria-atomic',
  'aria-autocomplete',
  'aria-braillelabel',
  'aria-brailleroledescription',
  'aria-busy',
  'aria-checked',
  'aria-colcount',
  'aria-colindex',
  'aria-colindextext',
  'aria-colspan',
  'aria-controls',
  'aria-current',
  'aria-describedby',
  'aria-description',
  'aria-details',
  'aria-disabled',
  'aria-errormessage',
  'aria-expanded',
  'aria-flowto',
  'aria-haspopup',
  'aria-hidden',
  'aria-invalid',
  'aria-keyshortcuts',
  'aria-label',
  'aria-labelledby',
  'aria-level',
  'aria-live',
  'aria-multiline',
  'aria-multiselectable',
  'aria-orientation',
  'aria-owns',
  'aria-placeholder',
  'aria-posinset',
  'aria-pressed',
  'aria-readonly',
  'aria-relevant',
  'aria-required',
  'aria-roledescription',
  'aria-rowcount',
  'aria-rowindex',
  'aria-rowindextext',
  'aria-rowspan',
  'aria-selected',
  'aria-setsize',
  'aria-sort',
  'aria-valuemax',
  'aria-valuemin',
  'aria-valuenow',
  'aria-valuetext',
  'role',
]

/**
 * Returns an array of aria attributes that should be copied from
 * the shadow host element to a target within the light DOM.
 *
 * @param element The element that the attributes should be copied from.
 */
export const inheritAriaAttributes = (
  element: Element,
): ReturnType<typeof inheritAttributes> =>
  inheritAttributes(element, ariaAttributes)

const isProduction = process.env.NODE_ENV === 'production'
const prefix = 'Invariant failed'

/**
 * Throws an error if the value is falsy.
 *
 * @param value Value to test.
 * @param message Optional message to display in development builds.
 */
export function invariant(value: unknown, message?: string): asserts value {
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (value) return
  if (isProduction) throw new Error(prefix)

  const error = message === undefined ? prefix : `${prefix}: ${message}`
  throw new Error(error)
}

/**
 * From an object pick a set of keys.
 *
 * @param object Object to pick from.
 * @param keys They keys to pick.
 * @returns New object with only the picked keys.
 */
export const pick = <T, K extends keyof T>(
  object: T,
  ...keys: K[]
): Pick<T, K> => {
  const returnValue: Partial<Pick<T, K>> = {}
  for (const key of keys) returnValue[key] = object[key]
  return returnValue as Pick<T, K>
}

export type QdsFocusEventDetail = Pick<FocusEvent, 'relatedTarget'>
export type QdsInputEventDetail = Pick<
  InputEvent,
  'data' | 'inputType' | 'isComposing'
>
export const pickFocusEventAttributes = (
  event: FocusEvent,
): QdsFocusEventDetail => pick(event, 'relatedTarget')
export const pickInputEventAttributes = (
  event: InputEvent,
): QdsInputEventDetail => pick(event, 'data', 'inputType', 'isComposing')

const getConversionFactor = (
  element: Element,
  unit: string,
): number | undefined => {
  switch (unit) {
    case 'cm': {
      return 96 / 2.54
    }
    case 'em': {
      return Number.parseFloat(
        globalThis.getComputedStyle(
          element.parentElement ?? document.documentElement,
        ).fontSize,
      )
    }
    case 'in': {
      return 96
    }
    case 'mm': {
      return 96 / 25.4
    }
    case 'pc': {
      return 16
    }
    case 'pt': {
      return 96 / 72
    }
    case 'px': {
      return 1
    }
    case 'rem': {
      return Number.parseFloat(
        globalThis.getComputedStyle(document.documentElement).fontSize,
      )
    }
    default: {
      return undefined
    }
  }
}

export const propertyToPx = (
  element: Element,
  property: string,
): number | undefined => {
  const value = globalThis.getComputedStyle(element).getPropertyValue(property)
  const number = Number.parseFloat(value)
  if (Number.isNaN(number)) return undefined
  const unit = /\s*[\d+.-]*\s*(.*)/.exec(value)?.[1] ?? ''

  const conversionFactor = getConversionFactor(element, unit) ?? 1
  return number * conversionFactor
}

export const ignorePromise = <T>(promise: Promise<T>): void => {
  promise.catch(() => {
    invariant(false)
  })
}

export const isTouchDevice = (): boolean =>
  'ontouchstart' in globalThis ||
  ('maxTouchPoints' in globalThis && globalThis.navigator.maxTouchPoints > 0)

export const roundByDPR = (value: number): number => {
  const dpr = devicePixelRatio || 1
  return Math.round(value * dpr) / dpr
}

/**
 * Checks if an element's content is overflowing.
 *
 * @param element The element to check.
 * @returns `true` if overflowing, `false` otherwise.
 */
export const isOverflowing = (element: Element): boolean => {
  const { clientHeight, clientWidth, scrollHeight, scrollWidth } = element
  return scrollHeight > clientHeight + 1 || scrollWidth > clientWidth
}

export const determineLinkIcon = (
  href?: URL | string,
  target?: string,
): LinkIcon | undefined => {
  if (href === undefined) return undefined

  let link
  try {
    link = new URL(href, globalThis.location.href)
  } catch {
    return undefined
  }
  const isExternalLink = link.origin !== globalThis.location.origin
  const opensInNewTab = target === '_blank'

  if (isExternalLink)
    return opensInNewTab ? 'external-new-tab-link' : 'external-link'
  return opensInNewTab ? 'new-tab-link' : undefined
}

export function attachInternals(this: unknown): ElementInternals {
  // @ts-expect-error: This is very unsafe but it's not possible to do this in
  //                   Stencil.js without creating a form-associated custom
  //                   element.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
  return this.attachInternals()
}

export const resolveTarget = (
  target?: ReferenceElement | string | (() => ReferenceElement | string),
): ReferenceElement | undefined => {
  const resolvedTarget = typeof target === 'function' ? target() : target
  if (typeof resolvedTarget !== 'string') return resolvedTarget

  const element = document.querySelector(resolvedTarget)
  if (element === null) throw new Error('String must resolve to an Element.')
  return element
}

export type OffsetValue = Except<
  Exclude<
    LibraryOffsetOptions,
    // eslint-disable-next-line @typescript-eslint/ban-types
    Function | number
  >,
  'mainAxis'
>
export type OffsetFunction = (state: MiddlewareState) => OffsetValue
export type OffsetOptions = OffsetFunction | OffsetValue
